import waRequest from './wa-request';

export const apiBase = '/sites/v1';

export const personsBase = `${apiBase}/sites/persons`;

/**
 * @typedef SiteCustomFieldFilter
 * @property {string} id - Custom field ID (UUID).
 * @property {string[]} [valuesEqual] - Exact match filter values.
 * @property {string[]} [valuesLike] - Like-search values (min length 2).
 */

/**
 * @typedef SiteFilter
 * @property {'NONE'|'ALERT'|'INCIDENT'} [applyVulnerabilityRadius='NONE']
 * @property {string} [bulkLoadId]
 * @property {string[]} [cityNameLike]
 * @property {{ id: number, requestId: string }} [cluster]
 * @property {string[]} [countryNameLike]
 * @property {SiteCustomFieldFilter[]} [customFields]
 * @property {string} [distanceFromPolygon] - GeoJSON polygon.
 * @property {string[]} [excludePolygons]
 * @property {string} [identifierLike]
 * @property {boolean} [includeSubOrganizations=true]
 * @property {number[]} [locationIds]
 * @property {string} [nameLike]
 * @property {string[]} [notesLike]
 * @property {number[]} [orLocationIds]
 * @property {string[]} [organizationIds]
 * @property {string[]} [polygons]
 * @property {string[]} [provinceNameLike]
 * @property {string} [quickSearch]
 * @property {string} [siteIdGreaterThan]
 * @property {string[]} [siteIds]
 * @property {string[]} [siteTypeIds]
 * @property {string} [siteTypeNameLike]
 */

/**
 * Returns Site type Data, including custom if org included.
 *
 *
 * @param {Object} config
 * @param {Object} config.params Querystring parameters
 * @param {string=} config.params.organizationId uuid The organization id to include custom site types for.
 * @param {string=} [config.params.locale=en] The requested language to return data in. ISO 639-1 format.
 *
 * @returns {Promise}
 * */
function getSiteTypes(config) {
  const {params} = config;

  const url = `${apiBase}/sites/siteTypes`;

  return waRequest.requestAuthenticated({url: url, method: 'get', params});
}

function addSite({data, signal}) {
  const url = `${apiBase}/sites`;

  return waRequest.requestAuthenticated({url: url, method: 'put', data, signal});
}

function updateSite({data, signal}) {
  return addSite({data, signal});
}

function getSitesSearchList({params, data, signal}) {
  const url = `${apiBase}/sites/searches`;

  return waRequest.requestAuthenticated({url, method: 'post', params, data, signal});
}

function getSecuritySearchList({params, data, signal}) {
  const url = `${apiBase}/physical-security/searches`;

  return waRequest.requestAuthenticated({url, method: 'post', params, data, signal});
}

function getSiteSearchHistory() {
  const url = `${apiBase}/sites/searches/history`;

  return waRequest.requestAuthenticated({url, method: 'get'});
}

function getSitesPropertySearch({pathParams: {propertyName}, params: {startsWith}, signal}) {
  const url = `${apiBase}/sites/properties/${propertyName}`;

  return waRequest.requestAuthenticated({
    url,
    method: 'get',
    signal,
    params: {
      startsWith,
    },
  });
}

function initiateSitesExport(configData) {
  const {
    pathParams,
    data: {filter},
  } = configData;

  const url = `${apiBase}/sites/exports`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    params: pathParams,
    data: filter,
  });
}

function getSiteExportStatus({id, successCallback}) {
  const url = `${apiBase}/sites/exports/${id}`;

  return waRequest.requestWithRetryUntilSuccess(
    {
      url: url,
      method: 'get',
    },
    successCallback,
  );
}

function getSiteById({pathParams: {siteId}, params: {locale}, signal}) {
  const url = `${apiBase}/sites/${siteId}`;

  return waRequest.requestAuthenticated({
    url,
    method: 'get',
    signal,
    params: {
      locale,
    },
  });
}

/**
 * Bulk edits multiple sites in a single operation (update, delete, etc.).
 *
 * @param {Object} args
 * @param {Object} [args.pathParams] - Not used for this endpoint.
 * @param {Object} [args.params] - Not used for this endpoint.
 *
 * @param {Object} args.data - Bulk edit payload.
 * Basic properties being updated
 * @param {string} [args.data.siteTypeId] - Site type ID to assign to all selected sites (UUID).
 * @param {string} [args.data.organizationId] - Organization ID that the sites will be associated with (UUID).
 * @param {string} [args.data.notes] - Notes about the bulk edit of sites.
 * Custom fields
 * @param {SiteCustomFieldFilter[]} [args.data.customFields] - Custom field values to apply to the selected sites.
 * Preferences
 * @param {Object} [args.data.preferences] - Notification preference overrides to apply to each site.
 * @param {string[]} [args.data.preferences.alertCategoryIds] - Alert category IDs to receive notifications for.
 * @param {('INFORMATIONAL'|'CAUTION'|'WARNING'|'CRITICAL')[]} [args.data.preferences.alertSeverities] - Alert severities to notify on.
 * @param {Object} [args.data.preferences.alertVulnerabilityRadius] - Alert vulnerability radius settings.
 * @param {('MILES'|'KM')} args.data.preferences.alertVulnerabilityRadius.units - Units for the radius.
 * @param {number} args.data.preferences.alertVulnerabilityRadius.value - Radius value (max 1000km or ~621 miles).
 * @param {string[]} [args.data.preferences.incidentCategoryIds] - Incident category IDs to receive notifications for.
 * @param {('MINIMAL'|'LOW'|'MODERATE'|'HIGH'|'SEVERE'|'UNKNOWN')[]} [args.data.preferences.incidentSeverities] - Incident severities to notify on.
 * @param {Object} [args.data.preferences.incidentVulnerabilityRadius] - Incident vulnerability radius settings.
 * @param {('MILES'|'KM')} args.data.preferences.incidentVulnerabilityRadius.units - Units for the radius.
 * @param {number} args.data.preferences.incidentVulnerabilityRadius.value - Radius value (max 1000km or ~621 miles).
 * DELETE
 * @param {boolean} [args.data.deleted=false] - Set to `true` to delete all selected sites instead of updating them.
 * Bulk update selectors
 * @param {SiteFilter} [args.data.siteFilter] - Optional filter object used to select sites instead of specifying siteIds directly. Common site filter structure
 * @param {string[]} [args.data.siteIds] - Explicit list of site IDs to include in the bulk edit. Ignored if siteFilter is used and the implementation chooses to.
 *
 * @returns {Promise<Object>} A promise resolving to the bulk edit result, including which sites were modified or rejected.
 */
function updateBulkSites({data}) {
  const url = `${apiBase}/sites`;

  return waRequest.requestAuthenticated({url, method: 'patch', data});
}

function deleteSite({pathParams: {siteId}, signal}) {
  const url = `${apiBase}/sites/${siteId}`;

  return waRequest.requestAuthenticated({url, method: 'delete', signal});
}

function updateSitePersonAssociations({params, data}) {
  const url = `${apiBase}/sites/persons`;

  return waRequest.requestAuthenticated({url, method: 'post', params, data});
}

function deleteSitePersonAssociations({params, data}) {
  const url = `${apiBase}/sites/persons`;

  return waRequest.requestAuthenticated({
    url,
    method: 'post',
    params,
    data: {
      ...data,
      isDelete: true,
    },
  });
}

function initiateSitesOccupantsExport(configData) {
  const {
    pathParams,
    data: {filter},
  } = configData;

  const {
    organizationId,
    locale,
    createUrl,
    sort,
    includeSiteType,
    includePhone,
    includeEmail,
    includeOrganization,
    includeCoordinates,
    includeAddress,
    exportFormat,
  } = pathParams;

  const url = `${apiBase}/sites/occupants/exports`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    params: {
      organizationId,
      locale,
      createUrl,
      sort,
      includeSiteType,
      includePhone,
      includeEmail,
      includeOrganization,
      includeCoordinates,
      includeAddress,
      exportFormat,
    },
    data: filter,
  });
}

function initiateSitePointsOfContactExport(configData) {
  const {
    pathParams: {siteId, ...rest},
    data,
  } = configData;

  const {organizationId, locale, createUrl, sort} = rest;

  const url = `${apiBase}/sites/${siteId}/pocs/exports`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    params: {
      organizationId,
      locale,
      createUrl,
      sort,
    },
    data,
  });
}

function initiateSiteOccupantsExport(configData) {
  const {
    pathParams: {siteId, ...rest},
    data,
  } = configData;

  const {organizationId, locale, createUrl, sort} = rest;

  const url = `${apiBase}/sites/${siteId}/occupants/exports`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    params: {
      organizationId,
      locale,
      createUrl,
      sort,
    },
    data,
  });
}

function getSitesGeodata({params: {locale, zoom}, data, signal}) {
  const url = `${apiBase}/sites/cluster-searches`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    params: {
      locale,
      zoom,
    },
    data,
    signal,
  });
}

function getSitePersons({params: {page = 0, size = 25, sort, locale}, data, signal}) {
  return waRequest.requestAuthenticated({
    url: `${personsBase}/searches`,
    method: 'post',
    params: {
      page,
      size,
      sort,
      locale,
    },
    data,
    signal,
  });
}

function getPersonSites({
  pathParams: {personId},
  params: {page = 0, size = 25, sort, locale, sitePersonType},
  data,
  signal,
}) {
  return waRequest.requestAuthenticated({
    url: `${apiBase}/sites/person/${personId}`,
    method: 'get',
    params: {
      page,
      size,
      sort,
      locale,
      sitePersonType,
    },
    data,
    signal,
  });
}

function getSiteSummaries({data: {organizationIds, polygons, excludePolygons, locationIds, siteTypeIds}, signal}) {
  const url = `${apiBase}/sites/summaries`;

  return waRequest.requestAuthenticated({
    url,
    method: 'post',
    data: {
      organizationIds,
      polygons,
      excludePolygons,
      locationIds,
      siteTypeIds,
    },
    signal,
  });
}

/**
 * Get list of distinct persons associated to sites
 *
 * @param page
 * @param size
 * @param sort
 * @param locale
 * @param data
 * @param signal
 * @returns {Promise}
 */
function getSitesDistinctPersons({params: {page = 0, size = 25, sort, locale}, data, signal}) {
  return waRequest.requestAuthenticated({
    url: `${personsBase}/distinctPersonSearches`,
    method: 'post',
    params: {
      page,
      size,
      sort,
      locale,
    },
    data,
    signal,
  });
}

function getBulkUploadsList({params: {size = 25, page = 0, sort = 'createdAt,desc'}, data: {organizationIds}, signal}) {
  const url = `${apiBase}/bulk-loads/searches`;
  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    signal,
    params: {
      size,
      page,
      sort,
    },
    data: {
      organizationIds,
    },
  });
}

function getBulkUploadsTemplates(params) {
  const url = `${apiBase}/bulk-loads/templates`;

  return waRequest.requestAuthenticated({url: url, method: 'get', responseType: 'blob', params});
}

function uploadFile({params, data}) {
  const url = `${apiBase}/bulk-loads`;

  return waRequest.requestAuthenticated({url: url, method: 'post', params, data});
}

function getFileDetails(id, successCallback) {
  const url = `${apiBase}/bulk-loads/${id}`;

  return waRequest.requestWithRetryUntilSuccess(
    {
      url: url,
      method: 'get',
    },
    successCallback,
    5000,
  );
}

function getFileErrors({pathParams: {fileId}}) {
  const url = `${apiBase}/bulk-loads/${fileId}/errors`;

  return waRequest.requestAuthenticated({url: url, method: 'get', responseType: 'blob'});
}

function getSiteDataFeeds(config) {
  const {
    params: {organizationId},
    signal,
  } = config;

  return waRequest.requestAuthenticated({
    url: `${apiBase}/site-data-feeds`,
    method: 'get',
    params: {
      organizationId,
    },
    signal,
  });
}

function createDataFeed(config) {
  const {
    data: {
      clientId,
      fileType,
      mapping,
      name,
      newSiteOrganizationId,
      notification,
      updateAction,
      enabled,
      connection,
      mappingEnabled,
    },
  } = config;

  const url = `${apiBase}/site-data-feeds`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'POST',
    data: {
      clientId,
      fileType,
      mapping,
      name,
      newSiteOrganizationId,
      notification,
      updateAction,
      enabled,
      connection,
      mappingEnabled,
    },
  });
}

function updateDataFeed(config) {
  const {
    pathParams: {id},
    data: {clientId, connection, enabled, fileType, mapping, name, newSiteOrganizationId, notification, updateAction},
  } = config;

  const url = `${apiBase}/site-data-feeds/${id}`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'PUT',
    data: {
      clientId,
      connection,
      enabled,
      fileType,
      mapping,
      name,
      newSiteOrganizationId,
      notification,
      updateAction,
      // mappingEnabled
    },
  });
}

function deleteDataFeed(config) {
  const {
    pathParams: {id},
  } = config;

  const url = `${apiBase}/site-data-feeds/${id}`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'DELETE',
  });
}

function testConnectionDataFeed({pathParams: {id}}) {
  const url = `${apiBase}/site-data-feeds/${id}/connection-status`;

  return waRequest.requestAuthenticated({url: url, method: 'GET'});
}

function generateSshKeyPair() {
  const url = `${apiBase}/site-data-feeds/ssh-keys`;

  return waRequest.requestAuthenticated({url: url, method: 'post'});
}

/**
 * Gets all site custom fields associated with a given organizationId
 *
 * @param {Object} config - The configuration object for the request.
 * @param {Object} config.pathParams - Path parameters for the request.
 * @param {string} config.pathParams.organizationId - The ID of the organization.
 * @param {Object} config.params - Query parameters for the request.
 * @param {string} [config.params.languageCode='en'] - The language code for localization.
 *
 * @returns {Promise} A promise that resolves with the site custom field configurations.
 */
function getSitesCustomFieldConfigs(config) {
  const {
    pathParams: {organizationId},
    params,
  } = config;

  const url = `${apiBase}/site-custom-fields/${organizationId}`;

  return waRequest.requestAuthenticated({url: url, method: 'get', params});
}

/**
 * Get filtered values for a specific site custom field.
 * Used for populating dropdown options when filtering sites by custom fields.
 *
 * @param {Object} config - The configuration object for the request.
 * @param {Object} config.pathParams - Path parameters for the request.
 * @param {string} config.pathParams.filterField - The filter field type (e.g., 'customFields').
 * @param {string} config.pathParams.fieldId - The UUID of the custom field.
 * @param {Object} [config.data] - Request body for search criteria.
 * @param {Array<string>} config.data.organizationIds - Array of organization UUIDs.
 * @param {boolean} [config.data.includeSubOrganizations=false] - Include sub-organizations.
 * @param {string} [config.data.valueLike] - Partial match string (2 char min).
 * @param {AbortSignal} [config.signal] - Optional abort signal.
 *
 * @returns {Promise} A promise that resolves with paginated custom field values.
 *
 * @example
 * // Search for values containing "@garda.com"
 * getCustomSitesFilterFieldValues({
 *   pathParams: {
 *     filterField: 'customFields',
 *     fieldId: '01d5830b-d218-4472-ae91-45dc855e19be'
 *   },
 *   data: {
 *     organizationIds: ['322ad7d7-99f1-46e2-b72b-18818e6e49f5'],
 *     valueLike: '@garda.com'
 *   }
 * });
 */
function getCustomSitesFilterFieldValues(config) {
  const {pathParams, params, data, signal} = config;
  const {filterField, fieldId} = pathParams;

  const url = `${apiBase}/sites/filter-values/${filterField}/${fieldId}/searches`;

  return waRequest.requestAuthenticated({url, method: 'POST', params, data, signal});
}

/**
 * Adds new or updates an existing site custom field.
 * Updates overwrite all existing site custom field configurations for organization.
 *
 * @param {Object} config - The configuration object for the request.
 * @param {Object} config.data - The data for the request.
 * @param {string} config.data.organizationId - The ID of the organization.
 * @param {Array<Object>} config.data.fields - The custom fields to be updated or created.
 *
 * @returns {Promise} A promise that resolves when the operation is complete.
 */
function putSitesCustomFieldConfigs(config) {
  const {data} = config;

  const url = `${apiBase}/site-custom-fields/`;

  return waRequest.requestAuthenticated({url: url, method: 'put', data});
}

function getSiteDataFeedsInfo() {
  const url = `${apiBase}/site-data-feeds/info`;

  return waRequest.requestAuthenticated({url: url, method: 'GET'});
}

function getDataFeed({pathParams: {id}, signal}) {
  const url = `${apiBase}/site-data-feeds/${id}`;

  return waRequest.requestAuthenticated({url: url, method: 'GET', signal});
}

function getSiteFilesOnServer(config) {
  const {
    pathParams: {id},
  } = config;

  const url = `${apiBase}/site-data-feeds/${id}/server-files`;

  return waRequest.requestAuthenticated({url: url, method: 'get'});
}

function deleteSitesFileOnServerFile(config) {
  const {
    pathParams: {id, fileName},
  } = config;

  const url = `${apiBase}/site-data-feeds/${id}/server-files/${fileName}`;

  return waRequest.requestAuthenticated({url: url, method: 'DELETE'});
}

/**
 * Endpoint to search for bulkLoads.
 *
 * @param {Object} config
 * @param {Object} config.params
 * @param {number} [config.params.page=0] Zero-based page index
 * @param {number} [config.params.size=100] The size of the page to be returned
 * @param {string} config.params.sort Sorting criteria in the format: field(,asc|desc)
 * @param {Object} config.data
 * @param {string[]} [config.data.organizationIds] Organization Ids to filter bulk load results on
 * @param {string} [config.data.siteDataFeedConfigurationId] A site data feed configuration id to filter bulk load results on. Only supported when the source is SFTP.
 * @param {string[]} [config.data.sources] (["MANUAL", "SFTP"] or ["TEST"]) Bulk load source to filter on.
 * */
function searchSitesDataFeedUploads(config) {
  const {params, data} = config;

  const url = `${apiBase}/bulk-loads/searches`;

  return waRequest.requestAuthenticated({url: url, method: 'POST', params, data});
}

/**
 * Initiate authenticated GET Data Feed Bulk Upload raw file to ${apiBase}/bulk-loads/${fileId}/raw
 *
 * @param {Object} config
 * @param {Object} config.pathParams
 * @param {string} config.pathParams.fileId The UUID of the bulk upload
 * @returns {Promise}
 */
function getDataFeedUploadRawFile(config) {
  const {
    pathParams: {fileId},
  } = config;

  const url = `${apiBase}/bulk-loads/${fileId}/raw`;

  return waRequest.requestAuthenticated({url: url, method: 'get', responseType: 'blob'});
}

function getDataFeedUploadSummaryFile(config) {
  const {
    pathParams: {fileId},
  } = config;

  const url = `${apiBase}/bulk-loads/${fileId}/summary`;

  return waRequest.requestAuthenticated({url: url, method: 'get', responseType: 'blob'});
}

/**
 *
 * Posts a manual or test file through the sftp config. Creates a bulk load associated to and with the sftp configs settings.
 *
 * @param {Object} config
 * @param {Object} config.pathParams
 * @param {string} config.pathParams.dataFeedId uuid
 * @param {Object} config.data
 * @param {boolean} config.data.isTest Whether this is a load file (wont commit sites).
 * @param {string<binary>} config.data.file file to upload
 *
 * @returns {Promise}
 * */
function uploadManualBulkLoadSiteData(config) {
  const {
    pathParams: {dataFeedId},
    data,
  } = config;

  const url = `${apiBase}/site-data-feeds/${dataFeedId}/bulk-loads`;

  return waRequest.requestAuthenticated({url: url, method: 'post', data});
}

function generatePgpKeyPair() {
  const url = `${apiBase}/site-data-feeds/pgp-keys`;

  return waRequest.requestAuthenticated({url: url, method: 'post'});
}

/**
 * Gets all custom site types associated with a given organizationId
 *
 * @param {Object} config - Request configuration.
 * @param {Object} config.pathParams - Path parameters.
 * @param {string} config.pathParams.organizationId - Organization ID.
 * @param {Object} config.params - Query parameters.
 * @param {string} [config.params.languageCode] - Language code for localization (default: "en").
 * @returns {Promise} Resolves with the list of custom types.
 */
function getCustomTypes(config) {
  const {
    pathParams: {organizationId},
    params: {languageCode},
  } = config;

  const url = `${apiBase}/custom-types/${organizationId}`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    params: {
      languageCode,
    },
  });
}

/**
 * Adds a new custom site type or updates an existing one.
 *
 * @param {Object} config - The configuration object for the request.
 * @param {Object} config.data - The data for the custom site type.
 * @param {string} config.data.iconUrl - The URL of the image associated with the site type.
 *   Example: `https://cdn.qa0.crisis24horizon.com/site_type_images/site-medical-facility-01d5830b-d218-4472-ae91-45dc855e19be.png`
 * @param {string} [config.data.id] - The ID of the site type (required only for updating an existing custom site type).
 *   Example: `01d5830b-d218-4472-ae91-45dc855e19be`
 * @param {Array<Object>} config.data.localizedNames - The localized names of the site type.
 * @param {string} config.data.localizedNames.languageCode - The language code for a localized name entry
 * @param {string} config.data.localizedNames.name - The name string for a localized name entry
 * @param {string} [config.data.organizationId] - The ID of the organization for which the site type is being created.
 *   Example: `0c42929f-af69-4732-afe8-e8e64a27303f`
 *
 * @returns {Promise} A promise that resolves when the custom site type is successfully created or updated.
 */
function postCustomType(config) {
  const {data} = config;

  const url = `${apiBase}/custom-types`;

  return waRequest.requestAuthenticated({url: url, method: 'POST', data});
}

/**
 * Deletes a custom type by its ID.
 *
 * @param {Object} config - The configuration object for the request.
 * @param {Object} config.pathParams - Path parameters for the request.
 * @param {string} config.pathParams.siteTypeId - The ID of the custom type to delete.
 * @returns {Promise} A promise that resolves when the custom type is deleted.
 */
function deleteCustomType(config) {
  const {
    pathParams: {siteTypeId},
  } = config;

  const url = `${apiBase}/custom-types/${siteTypeId}`;

  return waRequest.requestAuthenticated({url: url, method: 'DELETE'});
}

/**
 * List people at sites.
 *
 * @param {Object} config - The configuration object for the request.
 * @param {Object} config.params - Parameters for the request.
 * @returns {Promise} A promise that resolves with list of people at sites.
 */
function getPeopleAtSites(config) {
  const {data, params} = config;

  const url = `${apiBase}/sites/persons/searches`;

  return waRequest.requestAuthenticated({url: url, method: 'POST', params, data});
}

function getSiteTypesIcons(config) {
  const {
    pathParams: {organizationId},
  } = config;

  const url = `${apiBase}/custom-types/${organizationId}/icons`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
  });
}

function getPhysicalSecurityClusters({data, params, signal}) {
  const url = `${apiBase}/physical-security/cluster-searches`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'post',
    data,
    params,
    signal,
  });
}

/**
 * Gets a link to download a raw site data feed file
 *
 * @param {Object} config.pathParams
 * @param {string} config.pathParams.id The id of a bulk upload file
 * @param {string} config.pathParams.fileName The name of the file to download
 * @param {AbortSignal} config.signal use to cancel request
 *
 * @returns {Promise}
 * */
function getDataFeedFileOnServerRawDownloadUrl(config) {
  const {
    pathParams: {id, fileName},
    signal,
  } = config;

  const url = `${apiBase}/site-data-feeds/${id}/server-files/${fileName}/raw/download-url`;

  return waRequest.requestAuthenticated({url: url, method: 'get', signal});
}

/**
 * Gets a link to download a raw bulk upload file
 *
 *
 * @param {Object} config.params Querystring parameters
 * @param {string} config.params.organizationId The organization id of the user making the request. This is used for permission enforcement and templating, NOT for filtering.
 * @param {Object} config.pathParams
 * @param {string} config.pathParams.id The id of a bulk upload file
 * @param {AbortSignal} config.signal use to cancel request
 *
 * @returns {Promise}
 * */
function getDataFeedRawDownloadUrl(config) {
  const {
    pathParams: {id},
    params: {organizationId},
    signal,
  } = config;

  const url = `${apiBase}/bulk-loads/${id}/raw/download-url`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    params: {
      organizationId,
    },
    signal,
  });
}

/**
 * Gets a link to download a mapped bulk upload file
 *
 *
 * @param {Object} config.params Querystring parameters
 * @param {string} config.params.organizationId The organization id of the user making the request. This is used for permission enforcement and templating, NOT for filtering.
 * @param {Object} config.pathParams
 * @param {string} config.pathParams.id The id of a bulk upload file
 * @param {AbortSignal} config.signal use to cancel request
 *
 * @returns {Promise}
 * */
function getDataFeedMappedDownloadUrl(config) {
  const {
    pathParams: {id},
    params: {organizationId},
    signal,
  } = config;

  const url = `${apiBase}/bulk-loads/${id}/mapped/download-url`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    params: {
      organizationId,
    },
    signal,
  });
}

/**
 * Gets a link to download a bulk upload errors file
 *
 *
 * @param {Object} config.params Querystring parameters
 * @param {string} config.params.organizationId The organization id of the user making the request. This is used for permission enforcement and templating, NOT for filtering.
 * @param {Object} config.pathParams
 * @param {string} config.pathParams.id The id of a bulk upload file
 * @param {AbortSignal} config.signal use to cancel request
 *
 * @returns {Promise}
 * */
function getDataFeedErrorsDownloadUrl(config) {
  const {
    pathParams: {id},
    params: {organizationId},
    signal,
  } = config;

  const url = `${apiBase}/bulk-loads/${id}/errors/download-url`;

  return waRequest.requestAuthenticated({
    url: url,
    method: 'get',
    params: {
      organizationId,
    },
    signal,
  });
}

export default {
  addSite,
  updateSite,
  deleteSite,
  getSitesCustomFieldConfigs,
  putSitesCustomFieldConfigs,
  getSitesPropertySearch,
  getSitesSearchList,
  getSiteSearchHistory,
  getSiteTypes,
  getSiteById,
  updateBulkSites,
  initiateSitesExport,
  getSiteExportStatus,
  updateSitePersonAssociations,
  deleteSitePersonAssociations,
  initiateSitesOccupantsExport,
  initiateSitePointsOfContactExport,
  initiateSiteOccupantsExport,
  getSitesGeodata,
  getSitePersons,
  getPersonSites,
  getSiteSummaries,
  getSitesDistinctPersons,
  getBulkUploadsList,
  getBulkUploadsTemplates,
  uploadFile,
  getFileDetails,
  getFileErrors,
  getSiteDataFeeds,
  createDataFeed,
  updateDataFeed,
  deleteDataFeed,
  testConnectionDataFeed,
  generateSshKeyPair,
  getSiteDataFeedsInfo,
  getDataFeed,
  getSiteFilesOnServer,
  deleteSitesFileOnServerFile,
  searchSitesDataFeedUploads,
  getDataFeedUploadRawFile,
  getDataFeedUploadSummaryFile,
  uploadManualBulkLoadSiteData,
  generatePgpKeyPair,
  getCustomTypes,
  postCustomType,
  deleteCustomType,
  getPeopleAtSites,
  getCustomSitesFilterFieldValues,
  getSiteTypesIcons,
  getPhysicalSecurityClusters,
  getSecuritySearchList,
  getDataFeedFileOnServerRawDownloadUrl,
  getDataFeedRawDownloadUrl,
  getDataFeedMappedDownloadUrl,
  getDataFeedErrorsDownloadUrl,
};
